import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Reveal } from "./Reveal";
import { PROJECTS, BEHANCE_PROJECTS, CONTACT } from "../lib/data";

export const Projects = () => {
  const navigate = useNavigate();

  return (
    <section id="projects" className="py-24 lg:py-36" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-6">Selected work</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink max-w-2xl">
              Live sites, <span className="text-gradient">real results.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-ink-soft font-light max-w-xs">
              A selection of live websites I&rsquo;ve designed and built for clients across the US, UK and Europe.
            </p>
          </Reveal>
        </div>

        {/* Featured live-site case studies */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.1} className="h-full">
              <div className="group h-full glass rounded-3xl overflow-hidden hover:-translate-y-1.5 transition-transform duration-300 flex flex-col" data-testid={`project-card-${i}`}>
                <button onClick={() => navigate(`/case-study/${p.slug}`)} className="text-left block" data-testid={`project-open-${i}`}>
                  <div className="relative overflow-hidden m-2 rounded-2xl aspect-[16/10]">
                    <img src={p.image} alt={`${p.title} — website designed and built by Ricky Sokhi`} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105" />
                    <span className="absolute top-3 left-3 text-xs font-semibold uppercase tracking-wider glass text-ink rounded-full px-3 py-1">{p.clientType}</span>
                  </div>
                </button>
                <div className="p-6 pt-3 flex flex-col flex-1">
                  <h3 className="font-display font-semibold text-xl text-ink leading-tight mb-2 group-hover:text-brand transition-colors">{p.title}</h3>
                  <p className="text-ink-soft font-light text-[15px] flex-1">{p.result}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <button onClick={() => navigate(`/case-study/${p.slug}`)} className="inline-flex items-center gap-2 text-sm font-semibold text-gradient" data-testid={`project-casestudy-${i}`}>
                      View case study <ArrowUpRight className="w-4 h-4 text-brand" />
                    </button>
                    {p.live && (
                      <a href={p.live} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-soft hover:text-brand transition-colors" data-testid={`project-live-${i}`}>
                        Live site <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* More design work */}
        <div className="mt-24">
          <Reveal>
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-3">From my Behance</p>
                <h3 className="font-display font-bold text-2xl sm:text-3xl text-ink">More design work</h3>
              </div>
              <a href={CONTACT.behance} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-brand transition-colors" data-testid="behance-profile-link">
                View full profile <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BEHANCE_PROJECTS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 4) * 0.07} className="h-full">
                <a href={b.url} target="_blank" rel="noopener noreferrer" className="group block h-full glass rounded-3xl overflow-hidden hover:-translate-y-1.5 transition-transform duration-300" data-testid={`behance-card-${i}`}>
                  <div className="relative overflow-hidden m-2 rounded-2xl h-44">
                    <img src={b.image} alt={`${b.title} — ${b.tag} design by Ricky Sokhi`} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105" />
                    <span className="absolute top-3 left-3 text-xs font-semibold uppercase tracking-wider glass text-ink rounded-full px-3 py-1">{b.tag}</span>
                  </div>
                  <div className="p-5 pt-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-display font-semibold text-lg text-ink group-hover:text-brand transition-colors">{b.title}</h4>
                      <ArrowUpRight className="w-4 h-4 text-ink-soft group-hover:text-brand transition-colors" />
                    </div>
                    <p className="text-ink-soft font-light text-sm mt-2">{b.desc}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
